import { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

interface UserPermissions {
  tasks: boolean;
  approvals: boolean;
  analytics: boolean;
  rejectionAnalytics: boolean;
  labeling: {
    queue: boolean;
    schemas: boolean;
    workbench: boolean;
    review: boolean;
    models: boolean;
    datasets: boolean;
    activeLearning: boolean;
    modelQA: boolean;
    referenceImages: boolean;
  };
  admin: {
    workforce: boolean;
    userManagement: boolean;
  };
}

const defaultPermissions: UserPermissions = {
  tasks: false,
  approvals: false,
  analytics: false,
  rejectionAnalytics: false,
  labeling: {
    queue: false,
    schemas: false,
    workbench: false,
    review: false,
    models: false,
    datasets: false,
    activeLearning: false,
    modelQA: false,
    referenceImages: false,
  },
  admin: {
    workforce: false,
    userManagement: false,
  },
};

export function useUserPermissions() {
  const [permissions, setPermissions] = useState<UserPermissions>(defaultPermissions);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const loadUserPermissions = async (user: any) => {
      try {
        if (!user) {
          setPermissions(defaultPermissions);
          setIsSuperAdmin(false);
          setLoading(false);
          return;
        }

        // Check if user is super admin (has admin claim)
        // Force refresh the token to get the latest claims
        const token = await user.getIdTokenResult(true);
        const hasAdminClaim = !!token.claims.admin;
        setIsSuperAdmin(hasAdminClaim);
        
        console.log('🔍 [useUserPermissions] User auth state changed:', {
          uid: user.uid,
          email: user.email,
          hasAdminClaim,
          tokenExpiration: new Date(token.expirationTime * 1000).toLocaleString()
        });

        if (hasAdminClaim) {
          // Super admins have all permissions
          setPermissions({
            tasks: true,
            approvals: true,
            analytics: true,
            rejectionAnalytics: true,
            labeling: {
              queue: true,
              schemas: true,
              workbench: true,
              review: true,
              models: true,
              datasets: true,
              activeLearning: true,
              modelQA: true,
              referenceImages: true,
            },
            admin: {
              workforce: true,
              userManagement: true,
            },
          });
        } else {
          // Check user permissions from Firestore
          try {
            const userDoc = await getDoc(doc(db, 'adminUsers', user.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              setPermissions(userData.permissions || defaultPermissions);
            } else {
              // If no user document exists, use default permissions
              setPermissions(defaultPermissions);
            }
          } catch (error) {
            console.error('Error loading user permissions:', error);
            setPermissions(defaultPermissions);
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
        setPermissions(defaultPermissions);
      } finally {
        setLoading(false);
      }
    };

    const auth = getAuth();
    
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      await loadUserPermissions(user);
    });

    // Set up periodic token refresh to prevent expiration issues
    const refreshInterval = setInterval(async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          // Refresh the token every 30 minutes to prevent expiration
          await user.getIdToken(true);
          console.log('🔄 [useUserPermissions] Token refreshed');
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
      }
    }, 30 * 60 * 1000); // 30 minutes

    // Cleanup listener and interval on unmount
    return () => {
      unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const hasPermission = (path: string): boolean => {
    if (isSuperAdmin) return true;
    
    const keys = path.split('.');
    let current: any = permissions;
    
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return false;
      }
    }
    
    return Boolean(current);
  };

  return {
    permissions,
    loading,
    isSuperAdmin,
    hasPermission,
  };
}


